import { resolveStoredFileUrl } from "utils/storedFileUrl";
import {
  Add,
  AddShoppingCart,
  Remove,
  RestaurantMenu,
  Spa,
  Star,
  StarBorder,
} from "@mui/icons-material";
import { Card, IconButton, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftBadge from "components/SoftBadge";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import PropTypes from "prop-types";
import { memo, useCallback, useContext } from "react";
import { formatMoneyPunto } from "utils/formatters";
import { useModalPedido } from "../context/ModalPedidoContext";

const ProductCard = memo(
  ({
    prod,
    handleUpdate,
    handleAddDetalle,
    paraLlevar,
    pending = false,
    isFavorite = false,
    onToggleFavorite,
  }) => {
    const { handleSnackbar } = useContext(MessageManager);
    const { disabled } = useModalPedido();
    const cantidadDetalle = Number(prod?.cantidadDetalle || 0);
    const cantidadActual = Number(prod?.cantidad || 1);
    const hasImage = !!prod?.files?.[0]?.file;
    const productImageUrl = hasImage ? resolveStoredFileUrl(prod.files[0].file) : null;
    const esVegetariano = prod?.tipoProducto?.tipo === "Vegetariano";
    const esLibreDeGluten = prod?.tipoProducto?.tipo === "Libre de gluten";

    const getPrecioActual = (cantidad) => {
      if (paraLlevar) {
        if (prod?.cantidadPorMayor && cantidad + cantidadDetalle >= prod?.cantidadPorMayor) {
          return Number(prod?.precioPorMayorOnline ?? 0);
        }

        return Number(prod?.precioOnline ?? 0);
      }

      if (prod?.cantidadPorMayor && cantidad + cantidadDetalle >= prod?.cantidadPorMayor) {
        return Number(prod?.precioPorMayor ?? 0);
      }

      return Number(prod?.precioCarta ?? 0);
    };

    const precioMostrado = getPrecioActual(cantidadActual);

    const stockDisponible =
      prod?.stockCantidad === null || prod?.stockCantidad === undefined
        ? null
        : Number(prod?.stockCantidad || 0);
    const stockKnown = Number.isFinite(stockDisponible);
    const stockInsuficiente = !!prod?.stockInsuficiente;
    const cantidadTotalSeleccionada = cantidadActual + cantidadDetalle;
    const sinStock = !!prod?.disabledStock;
    const reachedStockLimit =
      stockKnown && stockDisponible > 0 && cantidadTotalSeleccionada >= stockDisponible;
    const exceededStockLimit =
      stockKnown && stockDisponible > 0 && cantidadTotalSeleccionada > stockDisponible;

    const handleIncrement = useCallback(() => {
      const nuevaCantidad = cantidadActual + 1;

      if (
        prod?.cantidadPorMayor &&
        cantidadDetalle < prod?.cantidadPorMayor &&
        nuevaCantidad + cantidadDetalle >= prod?.cantidadPorMayor
      ) {
        handleSnackbar("Se usara el ultimo precio por mayor registrado", "warning");
      }

      handleUpdate({
        ...prod,
        cantidad: nuevaCantidad,
        cantidadDetalle,
        precio: getPrecioActual(nuevaCantidad),
      });
    }, [cantidadActual, cantidadDetalle, handleSnackbar, handleUpdate, prod]);

    const handleDecrement = useCallback(() => {
      const nuevaCantidad = cantidadActual > 1 ? cantidadActual - 1 : 1;

      handleUpdate({
        ...prod,
        cantidad: nuevaCantidad,
        precio: getPrecioActual(nuevaCantidad),
      });
    }, [cantidadActual, handleUpdate, prod]);

    const renderFallbackMedia = () => (
      <SoftBox
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: esVegetariano
            ? "linear-gradient(180deg, #eef8ef 0%, #d9f0dd 100%)"
            : esLibreDeGluten
            ? "linear-gradient(180deg, #fff6e5 0%, #ffe1ad 100%)"
            : "linear-gradient(180deg, #f4f6f8 0%, #e5eaef 100%)",
        }}
      >
        {esVegetariano ? (
          <Spa sx={{ fontSize: 28, color: "#4f8a4a" }} />
        ) : (
          <RestaurantMenu sx={{ fontSize: 28, color: esLibreDeGluten ? "#c17d00" : "#5f6b7a" }} />
        )}
      </SoftBox>
    );

    const disabledAdd =
      disabled ||
      pending ||
      !prod?.nombre ||
      precioMostrado === 0 ||
      cantidadActual < 1 ||
      sinStock ||
      stockInsuficiente ||
      exceededStockLimit ||
      (stockKnown && stockDisponible > 0 && cantidadActual > stockDisponible);
    const disableDecrement = disabled || pending || cantidadActual <= 1;
    const disableIncrement =
      disabled || pending || sinStock || stockInsuficiente || reachedStockLimit;
    const getCircleButtonSx = (isDisabled, palette) => ({
      width: 38,
      height: 38,
      minWidth: 38,
      borderRadius: "50%",
      border: "none",
      outline: "none",
      padding: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      appearance: "none",
      backgroundColor: palette.background,
      color: "#fff",
      boxShadow: "none",
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      transition: "none",
      WebkitTapHighlightColor: "transparent",
      "&:hover": {
        backgroundColor: palette.background,
      },
      "&:focus": {
        outline: "none",
        boxShadow: "none",
      },
      "&:focus-visible": {
        outline: "none",
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: palette.background,
        boxShadow: "none",
      },
      "&:disabled": {
        backgroundColor: palette.background,
        color: "#fff",
        opacity: 0.5,
        cursor: "not-allowed",
      },
      "& svg": {
        color: "#fff",
      },
      "&:disabled svg": {
        color: "#fff",
      },
    });

    return (
      <Card
        sx={{
          borderRadius: 3,
          border: "1px solid rgba(0, 0, 0, 0.12)",
          boxShadow: "none",
          mr: 1,
        }}
      >
        <SoftBox display="flex" alignItems="center" gap={1.5} px={1.5} py={0.3}>
          <SoftBox
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              overflow: "hidden",
              flexShrink: 0,
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {hasImage ? (
              <SoftBox
                component="img"
                loading="lazy"
                src={productImageUrl}
                alt={prod?.nombre}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              renderFallbackMedia()
            )}
          </SoftBox>

          <SoftBox flex={1} minWidth={0}>
            <SoftTypography
              variant="h6"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {prod?.nombre}
            </SoftTypography>
            <SoftBox mt={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <SoftBadge
                color={precioMostrado === 0 ? "error" : "primary"}
                badgeContent={
                  precioMostrado === 0 ? "SIN PRECIO" : `$${formatMoneyPunto(precioMostrado)}`
                }
                container
              />
              {prod?.tipoProducto?.tipo ? (
                <SoftTypography variant="caption" color="text">
                  {prod.tipoProducto.tipo}
                </SoftTypography>
              ) : null}
            </SoftBox>
          </SoftBox>

          <SoftBox display="flex" alignItems="center" gap={1}>
            <Tooltip
              title={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
              placement="top"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => onToggleFavorite?.(prod)}
                  disabled={disabled || pending}
                  sx={{
                    color: "#f7bf2a",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    transition: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "&:focus": {
                      outline: "none",
                    },
                    "&:active": {
                      backgroundColor: "transparent",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(247, 191, 42, 0.45)",
                    },
                  }}
                >
                  {isFavorite ? <Star fontSize="medium" /> : <StarBorder fontSize="medium" />}
                </IconButton>
              </span>
            </Tooltip>

            {sinStock ? (
              <SoftBox
                sx={{
                  minWidth: 110,
                  height: 38,
                  px: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fafafa",
                }}
              >
                <SoftTypography variant="button" fontWeight="bold" color="text">
                  SIN STOCK
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftBox display="flex" alignItems="center" gap={0.5}>
                <SoftBox
                  component="button"
                  type="button"
                  onClick={handleDecrement}
                  disabled={disableDecrement}
                  sx={getCircleButtonSx(disableDecrement, {
                    background: "#b7c1d0",
                  })}
                >
                  <Remove fontSize="small" />
                </SoftBox>
                <SoftBox
                  sx={{
                    minWidth: 38,
                    height: 38,
                    borderRadius: "14px",
                    border: "1px solid rgba(15, 23, 42, 0.14)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <SoftTypography variant="button" fontWeight="bold">
                    {cantidadActual}
                  </SoftTypography>
                </SoftBox>
                <SoftBox
                  component="button"
                  type="button"
                  onClick={handleIncrement}
                  disabled={disableIncrement}
                  sx={getCircleButtonSx(disableIncrement, {
                    background: "#ffcc39",
                  })}
                >
                  <Add fontSize="small" />
                </SoftBox>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleAddDetalle(prod)}
                  disabled={disabledAdd}
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    transition: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "&:focus": {
                      outline: "none",
                    },
                    "&:active": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <AddShoppingCart sx={{ ml: 1 }} fontSize="small" />
                </IconButton>
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>
      </Card>
    );
  }
);

ProductCard.propTypes = {
  prod: PropTypes.object,
  handleUpdate: PropTypes.func,
  handleAddDetalle: PropTypes.func,
  paraLlevar: PropTypes.bool,
  pending: PropTypes.bool,
  isFavorite: PropTypes.bool,
  onToggleFavorite: PropTypes.func,
};

export default ProductCard;
